/**
 * PatternIQ Copilot Conversation Replay
 * ======================================
 * Modal to view full conversation with function calls and data
 */

import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Download, X, ThumbsUp, ThumbsDown, Minus } from 'lucide-react';

interface ConversationReplayProps {
  conversationId: string;
  messageId?: string;
  onClose: () => void;
}

export default function ConversationReplay({ 
  conversationId, 
  messageId,
  onClose 
}: ConversationReplayProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['copilot-conversation', conversationId],
    queryFn: async () => {
      const params = new URLSearchParams({ conversationId });
      if (messageId) params.append('messageId', messageId);
      
      const response = await fetch(`/api/copilot/feedback/conversation?${params}`);
      if (!response.ok) throw new Error('Failed to fetch conversation');
      const result = await response.json();
      return result.data;
    },
  });

  const handleExport = () => {
    if (!data) return;
    
    const exportData = {
      conversationId: data.conversationId,
      messages: data.messages,
      feedback: data.feedback,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${conversationId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Conversation Replay</DialogTitle>
              <DialogDescription>
                {data ? `${data.messageCount} messages • ${data.feedbackCount} feedback items` : 'Loading...'}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleExport} disabled={!data}>
                <Download className="h-4 w-4 mr-2" />
                Export JSON
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {isLoading && (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading conversation...</p>
          </div>
        )}

        {error && (
          <div className="py-8 text-center text-red-600">
            Error: {(error as Error).message}
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Conversation Metadata */}
            <Card className="p-4 bg-gray-50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Conversation ID:</span>
                  <code className="ml-2 text-xs bg-white px-2 py-1 rounded">{data.conversationId}</code>
                </div>
                <div>
                  <span className="font-medium">Messages:</span> {data.messageCount}
                </div>
              </div>
            </Card>

            {/* Feedback Summary */}
            {data.feedback && data.feedback.length > 0 && (
              <Card className="p-4 bg-blue-50 border-blue-200">
                <h3 className="font-medium mb-2">Feedback Received</h3>
                <div className="space-y-2">
                  {data.feedback.map((fb: any) => (
                    <div key={fb.id} className="flex items-start gap-3 text-sm">
                      <div className="mt-1">
                        {fb.rating === 1 ? (
                          <ThumbsUp className="h-4 w-4 text-green-600" />
                        ) : fb.rating === -1 ? (
                          <ThumbsDown className="h-4 w-4 text-red-600" />
                        ) : (
                          <Minus className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        {fb.feedbackText && (
                          <p className="text-gray-700 mb-1">{fb.feedbackText}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {fb.functionCalled && <Badge variant="outline">{fb.functionCalled}</Badge>}
                          <span>{new Date(fb.createdAt).toLocaleString()}</span>
                          {fb.responseTimeMs && <span>• {(fb.responseTimeMs / 1000).toFixed(2)}s</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Messages */}
            <div className="space-y-4">
              {data.messages.map((message: any, index: number) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      message.role === 'user'
                        ? 'bg-blue-100 border border-blue-200'
                        : message.role === 'assistant'
                        ? 'bg-gray-100 border border-gray-200'
                        : 'bg-yellow-50 border border-yellow-200'
                    }`}
                  >
                    {/* Role Badge */}
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={message.role === 'user' ? 'default' : 'secondary'}>
                        {message.role}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>

                    {/* Function Calls */}
                    {message.functionCalls && message.functionCalls.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <p className="text-xs font-medium text-gray-600 mb-2">Function Calls:</p>
                        {message.functionCalls.map((call: any, idx: number) => (
                          <div key={idx} className="bg-white rounded p-2 mb-2">
                            <code className="text-xs font-mono text-purple-700">{call.name}</code>
                            <details className="mt-1">
                              <summary className="text-xs text-gray-600 cursor-pointer">
                                Arguments
                              </summary>
                              <pre className="text-xs mt-1 p-2 bg-gray-50 rounded overflow-x-auto">
                                {JSON.stringify(call.arguments, null, 2)}
                              </pre>
                            </details>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Metadata */}
                    {message.metadata && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <details>
                          <summary className="text-xs text-gray-600 cursor-pointer">
                            Metadata
                          </summary>
                          <pre className="text-xs mt-1 p-2 bg-white rounded overflow-x-auto">
                            {JSON.stringify(message.metadata, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

